import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Palette, Sparkles, ArrowRight } from "lucide-react";
import { trackDesignWizardCTA } from "@/lib/analytics";

interface DesignWizardCTAProps {
  variant?: "inline" | "banner" | "card" | "compact";
  className?: string;
}

/**
 * Reusable Design Wizard CTA component for promoting the 3D Kitchen Designer
 * throughout the BBQ Island Liquidators site.
 */
export const DesignWizardCTA = ({ variant = "inline", className = "" }: DesignWizardCTAProps) => {
  const location = useLocation();
  
  const handleClick = () => {
    trackDesignWizardCTA(variant, location.pathname);
  };
  if (variant === "banner") {
    return (
      <section className={`py-12 bg-gradient-to-r from-[#2a2520] via-[#1a1a1a] to-[#2a2520] border-y border-orange-500/30 ${className}`}>
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-orange-500/20 rounded-lg flex items-center justify-center">
                <Palette className="w-7 h-7 text-orange-500" />
              </div>
              <div>
                <h3 className="text-xl md:text-2xl font-bold text-[#f5e6d3]">
                  Design Your Dream Kitchen in 3D
                </h3>
                <p className="text-[#f5e6d3]/70 text-sm md:text-base">
                  Use our interactive wizard to visualize your perfect outdoor kitchen
                </p>
              </div>
            </div>
            <Link to="/design-wizard" onClick={handleClick}>
              <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white font-bold group">
                <Sparkles className="w-5 h-5 mr-2" />
                Try Design Wizard
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    );
  }

  if (variant === "card") {
    return (
      <div className={`bg-gradient-to-br from-orange-500/20 to-orange-600/10 p-6 md:p-8 border border-orange-500/30 rounded-lg ${className}`}>
        <div className="flex items-start gap-4 mb-4">
          <div className="w-12 h-12 bg-orange-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
            <Palette className="w-6 h-6 text-orange-500" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-[#f5e6d3] mb-2">
              Design Your Kitchen in 3D
            </h3>
            <p className="text-[#f5e6d3]/70 text-sm mb-4">
              Choose your layout, add appliances, select finishes, and see your dream outdoor kitchen come to life with our interactive 3D designer.
            </p>
            <Link to="/design-wizard" onClick={handleClick}>
              <Button className="bg-orange-500 hover:bg-orange-600 text-white font-bold group">
                <Sparkles className="w-4 h-4 mr-2" />
                Start Designing
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (variant === "compact") {
    return (
      <Link 
        to="/design-wizard"
        onClick={handleClick}
        className={`inline-flex items-center gap-2 bg-orange-500/10 hover:bg-orange-500/20 border border-orange-500/30 hover:border-orange-500 px-4 py-2 rounded-lg transition-all group ${className}`}
      >
        <Palette className="w-4 h-4 text-orange-500" />
        <span className="text-[#f5e6d3] text-sm font-semibold">Try our 3D Design Wizard</span>
        <ArrowRight className="w-3 h-3 text-orange-400 group-hover:translate-x-1 transition-transform" />
      </Link>
    );
  }

  return (
    <div className={`flex flex-col sm:flex-row items-center gap-4 ${className}`}>
      <Link to="/design-wizard" onClick={handleClick}>
        <Button variant="outline" className="border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-white font-bold group">
          <Palette className="w-4 h-4 mr-2" />
          Try Our Design Wizard
          <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
        </Button>
      </Link>
    </div>
  );
};

export default DesignWizardCTA;
